import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { FormValues, SelectedIndexData } from '../../../types';
import { ONBOARDING_STEPS } from '../../../utils/constants';
import { OnboardingContextType } from '../types/onboarding.types';
import { useAuth } from '../../auth/context/AuthContext';

const DEFAULT_VALUES: FormValues = {};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [formValues, setFormValues] = useState<FormValues>({ ...DEFAULT_VALUES });
  const [selectedIndexData, setSelectedIndexData] = useState<SelectedIndexData>({ value: 0, origin: "other" });
  const [bottomValue, setBottomValueState] = useState<number>(0);
  
  const { isAdmin, isBorrower } = useAuth();
  const isProcessingTeamMode = isAdmin;
  const isBorrowerMode = isBorrower;

  const selectedIndex = +selectedIndexData.value;
  const currentStep = Object.keys(formValues).length;
  const forcedIndexRef = useRef<number | null>(null);

  const setValue = useCallback((key: string, value: string | number): void => {
    setFormValues(prevValues => {
      let newValues: FormValues;

      // If repair
      if (key === ONBOARDING_STEPS[1].text) {
        const isEditLoan = new URLSearchParams(window.location.search).get("editLoan") === '1';
        if (value === ONBOARDING_STEPS[1].options[1].text) {
          // Disable the long term (except on logged-in edit flow)
          if (!isEditLoan) {
            (ONBOARDING_STEPS[2].options[1] as any).isDisabled = true;
          } else {
            (ONBOARDING_STEPS[2].options[1] as any).isDisabled = false;
          }
          // And auto select the short term
          newValues = {
            ...prevValues,
            [key]: value,
            [ONBOARDING_STEPS[2].text]: ONBOARDING_STEPS[2].options[0].text
          };
        } else {
          (ONBOARDING_STEPS[2].options[1] as any).isDisabled = false;
          newValues = {
            ...prevValues,
            [key]: value,
          };
          if (prevValues[ONBOARDING_STEPS[2].text]) {
            newValues = {
              ...newValues,
              [ONBOARDING_STEPS[2].text]: ONBOARDING_STEPS[2].options[0].text
            };
          }
        }
      } else {
        newValues = { ...prevValues, [key]: value };
      }

      // Auto-select stabilized if long-term selected
      if (newValues["What is your ideal loan length?"] === ONBOARDING_STEPS[2].options[1].text) {
        newValues = {
          ...newValues,
          ["What condition is the property in?"]: ONBOARDING_STEPS[1].options[0].text
        };
      }

      const finalValue = newValues[key];
      if (finalValue === '' || finalValue === undefined) {
        const updatedValues = { ...newValues };
        delete updatedValues[key];
        newValues = updatedValues;
      }

      return newValues;
    });
  }, []);

  const setMultipleValues = useCallback((updates: Partial<FormValues>): void => {
    setFormValues(prevValues => ({
      ...prevValues,
      ...updates
    }));
  }, []);

  const _setSelectedIndex = useCallback((value: number): void => {
    setSelectedIndexData({ value, origin: "other" });
  }, []);

  const setSelectedIndexDataFn = useCallback((data: SelectedIndexData): void => {
    setSelectedIndexData(data);
  }, []);

  const setSelectedIndex = useCallback((index: number, options?: { force?: boolean }): void => {
    if (options?.force) {
      forcedIndexRef.current = index;
      _setSelectedIndex(index);
      return;
    }

    const currentStepValue = Object.keys(formValues).length;
    if (index <= currentStepValue || (index === ONBOARDING_STEPS.length && currentStepValue === ONBOARDING_STEPS.length)) {
      forcedIndexRef.current = null;
      _setSelectedIndex(index);
    }
  }, [formValues, _setSelectedIndex]);

  const setBottomValue = useCallback((value: number): void => {
    if (!(isProcessingTeamMode || isBorrowerMode)) {
      setBottomValueState(value);
    } else {
      setBottomValueState(value + 38);
    }
  }, [isProcessingTeamMode, isBorrowerMode]);

  const goToStep = useCallback((step: number): void => {
    setSelectedIndex(step);
  }, [setSelectedIndex]);

  const nextStep = useCallback(() => {
    setSelectedIndex(selectedIndex + 1);
  }, [selectedIndex, setSelectedIndex]);

  const previousStep = useCallback(() => {
    setSelectedIndex(Math.max(0, selectedIndex - 1));
  }, [selectedIndex, setSelectedIndex]);

  const reset = useCallback(() => {
    setFormValues({ ...DEFAULT_VALUES });
    setSelectedIndexData({ value: 0, origin: "other" });
    setBottomValueState(0);
  }, []);

  // Handle rawState from URL params
  useEffect(() => {
    const rawState = new URLSearchParams(window.location.search).get("state");
    const isEditLoan = new URLSearchParams(window.location.search).get("editLoan") === '1';
    if (rawState && !isEditLoan) {
      setValue("Where is the property located?", rawState.substring(1));
    }
  }, [setValue]);

  // Track when "Where is the property located?" value is set and prevent auto-advance
  const prevFormValuesRef = useRef<FormValues>(formValues);
  useEffect(() => {
    const stateStepText = "Where is the property located?";
    const hadStateValue = Boolean(prevFormValuesRef.current[stateStepText]);
    const hasStateValue = Boolean(formValues[stateStepText]);
    
    // If state value was just added (didn't have it before, has it now)
    // Don't auto-advance - user must click Continue
    if (!hadStateValue && hasStateValue) {
      prevFormValuesRef.current = formValues;
      return; // Don't auto-advance when state is first set
    }
    
    prevFormValuesRef.current = formValues;
    
    // Update selectedIndex when currentStep changes (normal behavior)
    if (forcedIndexRef.current !== null) {
      if (currentStep >= forcedIndexRef.current) {
        forcedIndexRef.current = null;
      } else {
        return;
      }
    }
    _setSelectedIndex(currentStep);
  }, [currentStep, _setSelectedIndex, formValues]);

  const value: OnboardingContextType = {
    formValues,
    currentStep,
    selectedIndex,
    selectedIndexData,
    bottomValue,
    setValue,
    setMultipleValues,
    setSelectedIndex,
    setSelectedIndexData: setSelectedIndexDataFn,
    setBottomValue,
    goToStep,
    nextStep,
    previousStep,
    reset,
    isCompleted: currentStep === ONBOARDING_STEPS.length,
    canProceed: true, // Add validation logic if needed
    totalSteps: ONBOARDING_STEPS.length
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
